import React from "react";

import { Repeat } from "apps/company/api/rest/company-dashboard-resilience";
import { useTranslation } from "lib/i18n/use-translation";

import { K, useDomainName, useRichText } from "./parts";
import { Domain, DOMAINS, formatChange, formatPoints } from "./resilience-view";
import styles from "./resilience.module.scss";

/**
 * How the Index moved for people assessed more than once, from their first
 * assessment to their latest. Nothing when Platform withholds that figure.
 */
const RepeatLine = ({ repeat }: { repeat: Repeat }) => {
  const { t, i18n } = useTranslation(["admin"]);
  const domainName = useDomainName();
  const rich = useRichText();
  if (repeat.index_change.suppressed) return null;

  const change = repeat.index_change.value;
  const figure = (shown: string) => (
    <strong>
      {t(`${K}.repeat.points`, { count: Math.abs(change), change: shown })}
    </strong>
  );
  const lead =
    change > 0
      ? rich(
          `${K}.repeat.improved`,
          {},
          { figure: figure(formatChange(change, i18n.language)) }
        )
      : change < 0
      ? rich(
          `${K}.repeat.fell`,
          {},
          { figure: figure(formatPoints(change, i18n.language)) }
        )
      : t(`${K}.repeat.steady`);
  const gains = repeat.domains_rose_most.map((domain) => {
    if (!(DOMAINS as readonly string[]).includes(domain)) {
      throw new Error(`Not a domain: ${domain}`);
    }
    return domainName(domain as Domain);
  });
  const strongest =
    gains.length === 2
      ? t(`${K}.repeat.strongestTwo`, { first: gains[0], second: gains[1] })
      : gains.length === 1
      ? t(`${K}.repeat.strongestOne`, { domain: gains[0] })
      : null;

  // Domains can rise while the Index as a whole does not.
  const key =
    strongest === null
      ? "sentence"
      : change > 0
      ? "sentenceWithGains"
      : "sentenceThoughRose";
  const sentence = rich(
    `${K}.repeat.${key}`,
    { gains: strongest },
    { lead: <>{lead}</> }
  );

  return <p className={styles["repeat"]}>{sentence}</p>;
};

export default RepeatLine;
